import { type JSX } from 'react';

import { Accordion, EloDivider } from '@plurall/elo-v3';
import { useLocation } from '@tanstack/react-router';

import { LocalNavigationEntry } from './LocalNavigationEntry';
import { LocalNavigationSubmenuItem } from './LocalNavigationSubmenuItem';
import { getAccordionKey, getDirectEntry, hasChildren, isItemActive } from '../helpers/localNavigationItemHelpers';
import { type LocalNavigationMenuSection } from '../hooks/useLocalNavigationMenuSections';
import { type UtilityAction } from '../types/localNavigationActions';
import {
  BaseNavSeparatorWrapperFactory,
  NavItemFactory,
  NavSectionFactory,
  NavSectionTitleFactory,
} from './factories/local-navigation-section-factory';

type LocalNavigationSectionProps = {
  isExpanded: (accordionId: string) => boolean;
  onAction?: (action: UtilityAction) => void;
  section: LocalNavigationMenuSection;
  toggleAccordion: (accordionId: string) => void;
};

export function LocalNavigationSection({
  isExpanded,
  onAction,
  section,
  toggleAccordion,
}: LocalNavigationSectionProps): JSX.Element {
  const { pathname } = useLocation();

  const openKeyInSection =
    section.items.map((item) => getAccordionKey(section.title, item.id)).find((key) => isExpanded(key)) ?? null;

  return (
    <NavSectionFactory>
      <NavSectionTitleFactory>{section.title || ''}</NavSectionTitleFactory>

      <BaseNavSeparatorWrapperFactory
        className='group__divider'
        aria-hidden='true'
      />

      <Accordion.Root
        collapsible
        value={openKeyInSection ? [openKeyInSection] : []}
        onValueChange={(details) => {
          const next = details.value[0];
          toggleAccordion(next ?? openKeyInSection ?? '');
        }}>
        {section.items.map((item) => {
          const key = getAccordionKey(section.title, item.id);

          if (item.type === 'link') {
            return (
              <LocalNavigationEntry
                key={key}
                active={isItemActive(pathname, item)}
                className='local-navigation__link'
                item={item}
              />
            );
          }

          if (hasChildren(item)) {
            return (
              <LocalNavigationSubmenuItem
                key={key}
                accordionKey={key}
                isExpanded={isExpanded(key)}
                item={item}
                onAction={onAction}
                pathname={pathname}
              />
            );
          }

          const directEntry = getDirectEntry(item);
          if (!directEntry) return null;

          return (
            <NavItemFactory key={key}>
              <LocalNavigationEntry
                active={isItemActive(pathname, item)}
                className='local-navigation__link'
                directEntry={directEntry}
                menuItem={item}
              />
            </NavItemFactory>
          );
        })}
      </Accordion.Root>

      {section.isUtility && (
        <EloDivider
          borderRadius='full'
          marginTop='2'
          marginBottom='padding.4xlarge'
          size='medium'
        />
      )}
    </NavSectionFactory>
  );
}
